import { useState, useEffect, useRef, useCallback } from 'react';
import { getDocument, getCurrentExtraction, getProvenance } from '../services/api';

interface Document {
  id: string;
  original_filename: string;
  status: string;
  domain: string;
  created_at: string;
  [key: string]: any;
}

interface Extraction {
  id: string;
  document_id: string;
  version?: number;
  source: string;
  payload_json: any;
  created_at: string;
  schema_id?: string | null;
  schema_version?: string | null;
  prompt_version?: string | null;
  llm_config_version?: string | null;
  ocr_config_version?: string | null;
  [key: string]: any;
}

export interface ProvenanceFragment {
  snippet: string;
  page: number;
  bbox?: { x1: number; y1: number; x2: number; y2: number } | null;
}

// Vue "représentative" (1 fragment par champ) — consommée par les formulaires
// (badge p.N + tooltip). Rétro-compatible avec l'ancienne forme {snippet, page}.
interface Provenance {
  [field_path: string]: ProvenanceFragment;
}

// Vue complète : TOUS les fragments par champ. Un même field_path peut être
// justifié par plusieurs passages (parfois sur des pages différentes) — le
// viewer en dessine un rectangle chacun.
export interface ProvenanceFragments {
  [field_path: string]: ProvenanceFragment[];
}

interface UseDocumentResult {
  document: Document | null;
  extraction: Extraction | null;
  provenance: Provenance;
  provenanceFragments: ProvenanceFragments;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
  // Recharge la provenance pour une extraction donnée (≠ current quand
  // l'utilisateur visualise une autre version). La surbrillance doit suivre
  // l'extraction réellement affichée.
  loadProvenance: (extractionId: string | undefined) => void;
}

// Construit les deux vues de provenance à partir de la réponse API :
//  - fragments : tous les fragments par champ (dédup des doublons stricts)
//  - représentative : 1 fragment par champ, en préférant celui qui a un bbox
function buildProvenanceMaps(
  fields: Array<{
    field_path: string;
    text_snippet: string;
    page_number: number;
    bbox?: { x1: number; y1: number; x2: number; y2: number } | null;
  }>,
): { fragments: ProvenanceFragments; representative: Provenance } {
  const fragments: ProvenanceFragments = {};
  const seen = new Set<string>();
  fields.forEach((field) => {
    const fragment: ProvenanceFragment = {
      snippet: field.text_snippet,
      page: field.page_number,
      bbox: field.bbox,
    };
    const key = `${field.field_path}|${field.page_number}|${JSON.stringify(
      field.bbox ?? null,
    )}|${field.text_snippet}`;
    if (seen.has(key)) return;
    seen.add(key);
    (fragments[field.field_path] ||= []).push(fragment);
  });

  const representative: Provenance = {};
  for (const [fieldPath, frags] of Object.entries(fragments)) {
    representative[fieldPath] = frags.find((f) => f.bbox) ?? frags[0];
  }
  return { fragments, representative };
}

export function useDocument(documentId: string | undefined): UseDocumentResult {
  const [document, setDocument] = useState<Document | null>(null);
  const [extraction, setExtraction] = useState<Extraction | null>(null);
  const [provenance, setProvenance] = useState<Provenance>({});
  const [provenanceFragments, setProvenanceFragments] = useState<ProvenanceFragments>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refetchTrigger, setRefetchTrigger] = useState(0);

  // Use a ref to track the current request ID for proper cancellation
  const currentRequestIdRef = useRef(0);
  // Quelle extraction est actuellement reflétée par `provenance` — évite de
  // recharger inutilement quand on reste sur la même extraction.
  const loadedProvenanceForRef = useRef<string | undefined>(undefined);
  // Annule un éventuel fetch de provenance "à la demande" (changement de version).
  const provenanceAbortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    // Increment request ID to invalidate any in-flight requests
    const requestId = ++currentRequestIdRef.current;

    // Create AbortController for this specific request
    const abortController = new AbortController();

    const loadData = async () => {
      if (!documentId) {
        setIsLoading(false);
        return;
      }

      // Reset state for new document
      setIsLoading(true);
      setError(null);
      // Clear previous document data when switching documents
      setDocument(null);
      setExtraction(null);
      setProvenance({});
      setProvenanceFragments({});
      loadedProvenanceForRef.current = undefined;

      try {
        // Fetch document with abort signal
        const docData = await getDocument(documentId, abortController.signal);

        // Check if this request is still the current one
        if (requestId !== currentRequestIdRef.current) {
          return; // Stale request, ignore results
        }

        setDocument(docData);

        // Fetch current extraction. PROD1.4.6 hotfix : le backend retourne
        // 200 + null si le doc n'a pas encore d'extraction (état métier vide).
        try {
          const extractionData = await getCurrentExtraction(documentId, abortController.signal);

          if (requestId !== currentRequestIdRef.current) {
            return;
          }

          setExtraction(extractionData);

          // Fetch provenance for this extraction
          if (extractionData?.id) {
            try {
              const provenanceData = await getProvenance(extractionData.id, abortController.signal);

              if (requestId !== currentRequestIdRef.current) {
                return;
              }

              const { fragments, representative } = buildProvenanceMaps(provenanceData.fields);
              setProvenanceFragments(fragments);
              setProvenance(representative);
              loadedProvenanceForRef.current = extractionData.id;
            } catch (err: any) {
              if (err.name !== 'AbortError') {
                console.warn('No provenance data available:', err);
              }
            }
          }
        } catch (err: any) {
          if (err.name !== 'AbortError') {
            console.warn('No extraction available yet:', err);
          }
        }
      } catch (err: any) {
        // Ignore abort errors - they're expected when navigating away
        if (err.name === 'AbortError') {
          return;
        }

        if (requestId === currentRequestIdRef.current) {
          setError(err.message || 'Failed to load document');
        }
      } finally {
        // Only update loading state if this is still the current request
        if (requestId === currentRequestIdRef.current) {
          setIsLoading(false);
        }
      }
    };

    loadData();

    // Cleanup: abort any in-flight requests when documentId changes or component unmounts
    return () => {
      abortController.abort();
    };
  }, [documentId, refetchTrigger]);

  const refetch = useCallback(() => {
    setRefetchTrigger(prev => prev + 1);
  }, []);

  // Recharge la provenance pour l'extraction réellement affichée. Appelé par
  // la page quand l'utilisateur change de version (displayedExtraction.id).
  // No-op si déjà chargée pour cette extraction.
  const loadProvenance = useCallback((extractionId: string | undefined) => {
    if (!extractionId) {
      // Pas d'extraction affichée → vide la surbrillance.
      provenanceAbortRef.current?.abort();
      loadedProvenanceForRef.current = undefined;
      setProvenance({});
      setProvenanceFragments({});
      return;
    }
    if (loadedProvenanceForRef.current === extractionId) return;

    provenanceAbortRef.current?.abort();
    const abortController = new AbortController();
    provenanceAbortRef.current = abortController;
    loadedProvenanceForRef.current = extractionId;

    getProvenance(extractionId, abortController.signal)
      .then((provenanceData) => {
        if (abortController.signal.aborted) return;
        const { fragments, representative } = buildProvenanceMaps(provenanceData.fields);
        setProvenanceFragments(fragments);
        setProvenance(representative);
      })
      .catch((err: any) => {
        if (err.name === 'AbortError') return;
        console.warn('No provenance data available:', err);
        setProvenance({});
        setProvenanceFragments({});
      });
  }, []);

  return {
    document,
    extraction,
    provenance,
    provenanceFragments,
    isLoading,
    error,
    refetch,
    loadProvenance,
  };
}
